/**
 * Builds output metadata from a {@link QueryPlan}.
 *
 * Every compiler uses this so `outputFields` (names/types/kinds) stay identical
 * across engines. The pagination mode is passed in by the compiler because it
 * is family-specific (SQL → offset; search → offset or search_after).
 */
import type {
    QueryOutputField,
    QueryOutputMetadata,
} from '../compilers/compiler';
import type { QueryPlan } from '../plan/query-plan';

export function buildOutputMetadata(
    plan: QueryPlan,
    paginationMode: 'offset' | 'search_after',
): QueryOutputMetadata {
    const outputFields: QueryOutputField[] = [];

    // Grouped/aggregated output: group fields + aggregations.
    if (plan.grouping || plan.aggregations.length > 0) {
        for (const group of plan.grouping?.groups ?? []) {
            outputFields.push({
                name: group.outputName,
                kind: 'group',
                sourceField: group.field.field,
                type: group.field.type,
                ...(group.granularity
                    ? { granularity: group.granularity }
                    : {}),
            });
        }
        for (const agg of plan.aggregations) {
            outputFields.push({
                name: agg.outputName,
                kind: 'aggregation',
                sourceField: agg.field?.field ?? '',
                function: agg.fn,
                // Aggregations produce numbers (count/sum/avg/min/max).
                type: 'number',
            });
        }
    } else if (plan.projection) {
        // Row output: selected fields.
        for (const field of plan.projection.fields) {
            outputFields.push({
                name: field.qualifiedName(),
                kind: 'field',
                sourceField: field.qualifiedName(),
                type: field.type,
            });
        }
    }

    const pagination: QueryOutputMetadata['pagination'] =
        paginationMode === 'search_after'
            ? { mode: 'search_after', requiresStableSort: true }
            : { mode: 'offset' };

    return { outputFields, pagination };
}
